import { Sale } from './saleService';
import { Client } from './clientService';
import { DailyReport, ReportSummary } from './reportService';

export const utilityService = {
  formatCurrency: (amount: number): string => {
    return `KES ${amount.toLocaleString()}`;
  },

  formatDate: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  },

  calculateTotal: (sales: Sale[]): number => {
    return sales.reduce((sum, sale) => sum + sale.total, 0);
  },

  calculateTotalQuantity: (sales: Sale[]): number => {
    return sales.reduce((sum, sale) => sum + sale.quantity, 0);
  },

  calculateAveragePrice: (sales: Sale[]): number => {
    const totalQuantity = utilityService.calculateTotalQuantity(sales);
    return totalQuantity > 0 ? utilityService.calculateTotal(sales) / totalQuantity : 0;
  },

  exportSalesToCSV: (sales: Sale[], filename: string = 'sales_report.csv'): void => {
    const data = sales.map(sale => ({
      'Client Name': sale.clientName,
      'Quantity (L)': sale.quantity,
      'Price per Liter (KES)': sale.pricePerLiter,
      'Total (KES)': sale.total,
      'Date': utilityService.formatDate(sale.date)
    }));
    utilityService.exportToCSV(data, filename);
  },

  exportClientsToCSV: (clients: Client[], filename: string = 'clients_report.csv'): void => {
    const data = clients.map(client => ({
      'Name': client.name,
      'Phone Number': client.phoneNumber,
      'Collection Centre': client.collectionCentre,
      'Date Added': utilityService.formatDate(client.date)
    }));
    utilityService.exportToCSV(data, filename);
  },

  exportDailyReportToCSV: (report: DailyReport & { clients: Client[] }, filename: string = 'daily_report.csv'): void => {
    // Create summary data
    const summaryData = [{
      'Date': report.date,
      'Total Sales (KES)': report.totalSales,
      'Total Quantity (L)': report.totalQuantity,
      'Average Price (KES/L)': report.averagePrice
    }];

    // Create sales data
    const salesData = report.sales.map(sale => ({
      'Client Name': sale.clientName,
      'Quantity (L)': sale.quantity,
      'Price per Liter (KES)': sale.pricePerLiter,
      'Total (KES)': sale.total,
      'Date': utilityService.formatDate(sale.date)
    }));

    // Create clients data
    const clientsData = report.clients.map(client => ({
      'Name': client.name,
      'Phone Number': client.phoneNumber,
      'Collection Centre': client.collectionCentre,
      'Date Added': utilityService.formatDate(client.date)
    }));

    // Export each section as a separate CSV
    utilityService.exportToCSV(summaryData, `summary_${filename}`);
    utilityService.exportToCSV(salesData, `sales_${filename}`);
    utilityService.exportToCSV(clientsData, `clients_${filename}`);
  },

  exportDateRangeReportToCSV: (report: ReportSummary & { clients: Client[] }, filename: string = 'date_range_report.csv'): void => {
    // Create summary data
    const summaryData = [{
      'Total Sales (KES)': report.totalSales,
      'Total Quantity (L)': report.totalQuantity,
      'Average Price (KES/L)': report.averagePrice
    }];

    // Create sales data
    const salesData = report.sales.map(sale => ({
      'Client Name': sale.clientName,
      'Quantity (L)': sale.quantity,
      'Price per Liter (KES)': sale.pricePerLiter,
      'Total (KES)': sale.total,
      'Date': utilityService.formatDate(sale.date)
    }));

    // Create clients data
    const clientsData = report.clients.map(client => ({
      'Name': client.name,
      'Phone Number': client.phoneNumber,
      'Collection Centre': client.collectionCentre,
      'Date Added': utilityService.formatDate(client.date)
    }));

    // Export each section as a separate CSV
    utilityService.exportToCSV(summaryData, `summary_${filename}`);
    utilityService.exportToCSV(salesData, `sales_${filename}`);
    utilityService.exportToCSV(clientsData, `clients_${filename}`);
  },

  exportToCSV: (data: any[], filename: string) => {
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => row[header]).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}; 